/* eslint-disable no-underscore-dangle */
import React, { useEffect } from 'react';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { useApolloClient } from '@apollo/react-hooks';
import useUser from 'store/authentication/useUser';
import Filter from 'components/Form/Filter/Filter';
import {
  createaGetParamsToBeHidden,
  createParseInitialParams,
} from 'components/Presets/utils';
import {
  sanitizeFilterValues,
  sanitizeUrlValues,
} from 'components/Form/Filter/utils';
import {
  getFieldsFromStorage,
  clearFieldsFromStorage,
} from 'components/Exports/utils';
import { SUMMARY_COLUMNS } from 'components/Metrics/constants/columns';
import { PLACEMENT_METRICS_FOR_DISPLAY } from 'components/Placement/View/Hidrator';
import { getCountryName } from 'shared/metrics/utils';
import schema from './schema';
import List from './List';
import { PlacementsExportQuery } from './graphql';

const toMetricsDisplay = (columns) => {
  if (!columns) {
    return undefined;
  }
  const result = {};
  Object.entries(columns).forEach(([key, value]) => {
    if (!key.includes('__')) {
      result[key] = value.__column;
    }
  });

  return result;
};

const hasAny = (group) => {
  const iterate = (g) =>
    Object.values(g).some((field) => {
      if (field.__selected) {
        return true;
      }
      if (field.__columnGrouping) {
        return iterate(field);
      }
      return false;
    });
  return iterate(group);
};

const cbn = 'placement-export';

function PlacementExport() {
  const { user } = useUser();
  const apolloClient = useApolloClient();

  const fields = getFieldsFromStorage('placements');
  useEffect(
    () => () => {
      clearFieldsFromStorage('placements');
    },
    [],
  );
  const ignoreValues = ['orientation', 'presentation', 'listMode'];

  const parseInitialParams = createParseInitialParams(
    user,
    apolloClient,
    'placements',
  );
  const getParamsToBeHidden = createaGetParamsToBeHidden(apolloClient);

  const shouldRender = (field) => {
    if (!fields) {
      return true;
    }

    if (get(fields, `${field}.__columnGrouping`)) {
      return hasAny(get(fields, field));
    }

    return Boolean(get(fields, `${field}.__selected`));
  };
  const renderLabel = (field, defaultLabel) => {
    if (!fields) {
      return defaultLabel;
    }
    return get(fields, `${field}.__columnCustomName`) || defaultLabel;
  };

  return (
    <Filter
      allowSkipInUrl
      query={PlacementsExportQuery}
      schema={schema}
      variables={{
        columns: SUMMARY_COLUMNS,
        summaryMetricConfig:
          user.role !== 'external'
            ? [{ allMetrics: true }]
            : PLACEMENT_METRICS_FOR_DISPLAY,
      }}
      parseInitialParams={parseInitialParams}
      getParamsToBeHidden={getParamsToBeHidden}
      transform={sanitizeFilterValues(ignoreValues)}
      transformUrl={sanitizeUrlValues(ignoreValues)}
      transformVariables={(variables) => {
        const newVariables = cloneDeep(variables);

        if (variables.f?.metric?.country) {
          newVariables.country = getCountryName(variables.f.metric.country);
        } else {
          newVariables.country = 'global';
        }

        return newVariables;
      }}
      render={({ response, values }) =>
        response.data.length > 0 && (
          <List
            cbn={cbn}
            data={response.data}
            values={values}
            metricsDisplay={toMetricsDisplay(get(fields, 'columns'))}
            renderLabel={renderLabel}
            shouldRender={shouldRender}
          />
        )}
    />
  );
}

export default PlacementExport;
